import React, { useEffect, useMemo, useState } from "react";
import {
  Sparkles,
  KeyRound,
  Trash2,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Volume2,
  Mic,
  Wallet,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  PROVIDERS,
  providerLabel,
  fetchAiSettings,
  saveAiSettings,
  saveAiKey,
  deleteAiKey,
  listAiModels,
  testAiConnection,
  fetchAiUsage,
  aiErrorText,
  fmtUsd,
} from "../ai/aiApi";
import { SCOPE_LABELS, ALL_SCOPES } from "../ai/context";
import { ttsSupported, sttSupported, listVoices, speak } from "../ai/speech";

const field =
  "w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-xl border-2 border-gray-600 focus:border-cyan-500 outline-none transition-colors placeholder-gray-500 text-sm";
const btn =
  "px-3 py-1.5 rounded-lg text-xs font-semibold border flex items-center gap-1.5 disabled:opacity-50";

/**
 * Settings → AI companion: name, provider keys, preferred provider + model
 * (live list), Test, monthly budget, what it may read, voice, usage.
 * Cloud accounts only (keys are stored encrypted in Convex).
 */
const AiSettingsSection = ({ isOpen, onChanged }) => {
  const [s, setS] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [keyDrafts, setKeyDrafts] = useState({});
  const [showKey, setShowKey] = useState({});
  const [keyBusy, setKeyBusy] = useState(null);
  const [models, setModels] = useState([]);
  const [modelsBusy, setModelsBusy] = useState(false);
  const [modelsNote, setModelsNote] = useState(null);
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [usage, setUsage] = useState(null);
  const [cap, setCap] = useState("");
  const [override, setOverride] = useState({ in: "", out: "" });
  const [voices, setVoices] = useState([]);

  const load = async () => {
    try {
      const data = await fetchAiSettings();
      setS(data);
      setName(data.assistantName || "");
      setCap(String(data.monthlyCapUsd ?? 10));
      const o = data.priceOverrides && data.provider && data.model
        ? data.priceOverrides[`${data.provider}/${data.model}`]
        : null;
      setOverride(o ? { in: String(o.in), out: String(o.out) } : { in: "", out: "" });
      setError(null);
      if (onChanged) onChanged(data);
    } catch (e) {
      setError(aiErrorText(e, "Could not load AI settings."));
    }
  };
  const loadUsage = async () => {
    try {
      setUsage(await fetchAiUsage());
    } catch (e) {
      setUsage(null);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setTestResult(null);
    setModelsNote(null);
    load();
    loadUsage();
    if (ttsSupported()) {
      setVoices(listVoices());
      const onVoices = () => setVoices(listVoices());
      window.speechSynthesis.addEventListener("voiceschanged", onVoices);
      return () => window.speechSynthesis.removeEventListener("voiceschanged", onVoices);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const hasKey = (p) => !!(s && s.keys.some((k) => k.provider === p));
  const keyInfo = (p) => (s ? s.keys.find((k) => k.provider === p) : null);

  // Load the model list whenever the chosen provider has a key.
  useEffect(() => {
    if (!s || !s.provider || !hasKey(s.provider)) {
      setModels([]);
      return;
    }
    let cancelled = false;
    setModelsBusy(true);
    setModelsNote(null);
    listAiModels(s.provider)
      .then((r) => {
        if (cancelled) return;
        setModels(r.models || []);
        setModelsNote(r.cached ? "Model list from cache (refresh for a live list)." : null);
      })
      .catch((e) => !cancelled && setModelsNote(aiErrorText(e, "Could not load models.")))
      .finally(() => !cancelled && setModelsBusy(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s && s.provider, s && s.keys.length]);

  const patch = async (p) => {
    try {
      await saveAiSettings(p);
      await load();
    } catch (e) {
      setError(aiErrorText(e, "Could not save."));
    }
  };

  const saveKey = async (provider) => {
    const key = (keyDrafts[provider] || "").trim();
    if (!key) return;
    setKeyBusy(provider);
    setError(null);
    try {
      await saveAiKey(provider, key);
      setKeyDrafts((d) => ({ ...d, [provider]: "" }));
      // First key: make it the preferred provider.
      if (s && !s.provider) await saveAiSettings({ provider });
      await load();
    } catch (e) {
      setError(aiErrorText(e, "Could not save the key."));
    } finally {
      setKeyBusy(null);
    }
  };

  const removeKey = async (provider) => {
    setKeyBusy(provider);
    try {
      await deleteAiKey(provider);
      if (s && s.provider === provider) await saveAiSettings({ provider: null, model: null });
      await load();
    } catch (e) {
      setError(aiErrorText(e, "Could not remove the key."));
    } finally {
      setKeyBusy(null);
    }
  };

  const refreshModels = async () => {
    if (!s || !s.provider) return;
    setModelsBusy(true);
    setModelsNote(null);
    try {
      const r = await listAiModels(s.provider, true);
      setModels(r.models || []);
    } catch (e) {
      setModelsNote(aiErrorText(e, "Could not load models."));
    } finally {
      setModelsBusy(false);
    }
  };

  const runTest = async () => {
    if (!s || !s.provider || !s.model) return;
    setTestBusy(true);
    setTestResult(null);
    try {
      const r = await testAiConnection(s.provider, s.model);
      setTestResult(r);
      loadUsage();
    } catch (e) {
      setTestResult({ ok: false, error: aiErrorText(e, "Test failed.") });
    } finally {
      setTestBusy(false);
    }
  };

  const saveOverride = async () => {
    if (!s || !s.provider || !s.model) return;
    const key = `${s.provider}/${s.model}`;
    const next = { ...(s.priceOverrides || {}) };
    const inP = parseFloat(override.in);
    const outP = parseFloat(override.out);
    if (Number.isFinite(inP) && Number.isFinite(outP)) next[key] = { in: inP, out: outP };
    else delete next[key];
    await patch({ priceOverrides: JSON.stringify(next) });
  };

  const selectedModel = useMemo(
    () => (s && models.find((m) => m.id === s.model)) || null,
    [s, models],
  );
  const scopes = (s && s.scopes) || ALL_SCOPES;
  const toggleScope = (k) => {
    const next = scopes.includes(k) ? scopes.filter((x) => x !== k) : [...scopes, k];
    patch({ scopes: next });
  };

  return (
    <div className="space-y-4 border-t border-gray-700 pt-6">
      <h3 className="text-yellow-400 font-semibold flex items-center gap-2">
        <Sparkles className="w-5 h-5" />
        AI companion
      </h3>
      <p className="text-xs text-gray-400">
        Bring your own API key. Keys are encrypted on the server and never sent
        to this browser. The companion only reads the panels you allow below,
        and every change it proposes needs your confirmation.
      </p>

      {error && (
        <div className="p-3 rounded-xl border-2 bg-red-600/10 border-red-500/30 text-red-300 text-sm">
          {error}
        </div>
      )}

      {s && (
        <>
          {/* Name */}
          <div>
            <label className="block text-cyan-300 text-xs font-semibold mb-1">Name</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={name}
                maxLength={40}
                onChange={(e) => setName(e.target.value)}
                className={field}
                placeholder="xyz Ai"
              />
              <button
                type="button"
                disabled={name.trim() === (s.assistantName || "")}
                onClick={() => patch({ assistantName: name.trim() || "xyz Ai" })}
                className={`${btn} bg-cyan-500/20 text-cyan-200 border-cyan-500/40 hover:bg-cyan-500/30`}
              >
                Rename
              </button>
            </div>
          </div>

          {/* Keys */}
          <div className="space-y-2">
            <label className="block text-cyan-300 text-xs font-semibold flex items-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5" />
              API keys
            </label>
            {PROVIDERS.map((p) => {
              const info = keyInfo(p.id);
              return (
                <div key={p.id} className="rounded-xl bg-gray-700/40 border border-gray-600 p-2.5">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-sm text-cyan-100 font-semibold">{p.label}</span>
                    {info ? (
                      <span className="text-[11px] text-green-300">key ending …{info.hint}</span>
                    ) : (
                      <span className="text-[11px] text-gray-500">no key · {p.site}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showKey[p.id] ? "text" : "password"}
                        value={keyDrafts[p.id] || ""}
                        onChange={(e) => setKeyDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                        placeholder={info ? "Paste a new key to replace" : p.keyHint}
                        autoComplete="off"
                        className={`${field} pr-9`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowKey((d) => ({ ...d, [p.id]: !d[p.id] }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-300 p-1"
                        aria-label={showKey[p.id] ? "Hide key" : "Show key"}
                      >
                        {showKey[p.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <button
                      type="button"
                      disabled={keyBusy === p.id || !(keyDrafts[p.id] || "").trim()}
                      onClick={() => saveKey(p.id)}
                      className={`${btn} bg-cyan-500/20 text-cyan-200 border-cyan-500/40 hover:bg-cyan-500/30`}
                    >
                      {keyBusy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      Save
                    </button>
                    {info && (
                      <button
                        type="button"
                        disabled={keyBusy === p.id}
                        onClick={() => removeKey(p.id)}
                        className={`${btn} bg-red-600/10 text-red-300 border-red-500/30 hover:bg-red-600/20`}
                        title="Remove key"
                        aria-label={`Remove ${p.label} key`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Provider + model */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-cyan-300 text-xs font-semibold mb-1">Preferred provider</label>
              <select
                value={s.provider || ""}
                onChange={(e) => patch({ provider: e.target.value || null, model: null })}
                className={field}
              >
                <option value="">— choose —</option>
                {PROVIDERS.filter((p) => hasKey(p.id)).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-cyan-300 text-xs font-semibold mb-1 flex items-center justify-between">
                <span>Model</span>
                {s.provider && hasKey(s.provider) && (
                  <button
                    type="button"
                    onClick={refreshModels}
                    disabled={modelsBusy}
                    className="text-[11px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1 disabled:text-gray-500"
                  >
                    <RefreshCw className={`w-3 h-3 ${modelsBusy ? "animate-spin" : ""}`} />
                    refresh
                  </button>
                )}
              </label>
              <select
                value={s.model || ""}
                disabled={!s.provider || modelsBusy}
                onChange={(e) => patch({ model: e.target.value || null })}
                className={field}
              >
                <option value="">{modelsBusy ? "Loading models…" : "— choose —"}</option>
                {s.model && !models.some((m) => m.id === s.model) && (
                  <option value={s.model}>{s.model}</option>
                )}
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name && m.name !== m.id ? `${m.name} (${m.id})` : m.id}
                  </option>
                ))}
              </select>
              {modelsNote && <p className="text-[11px] text-gray-500 mt-1">{modelsNote}</p>}
              {selectedModel && (selectedModel.inPrice != null || selectedModel.context) && (
                <p className="text-[11px] text-gray-500 mt-1">
                  {selectedModel.inPrice != null
                    ? `${fmtUsd(selectedModel.inPrice)} in / ${fmtUsd(selectedModel.outPrice)} out per 1M tokens`
                    : ""}
                  {selectedModel.context ? ` · ${selectedModel.context.toLocaleString()} ctx` : ""}
                </p>
              )}
            </div>
          </div>

          {/* Test */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={runTest}
              disabled={testBusy || !s.provider || !s.model || !hasKey(s.provider)}
              className={`${btn} bg-yellow-500 text-gray-900 border-yellow-500 hover:bg-yellow-400`}
            >
              {testBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Test connection
            </button>
            <span className="text-[11px] text-gray-500">
              Sends one tiny prompt: latency, tokens, cost and parameter support.
            </span>
          </div>
          {testResult && (
            <div
              className={`p-3 rounded-xl border text-xs space-y-1 ${
                testResult.ok
                  ? "bg-green-600/10 border-green-500/30 text-green-200"
                  : "bg-red-600/10 border-red-500/30 text-red-300"
              }`}
            >
              <div className="flex items-center gap-1.5 font-semibold">
                {testResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {testResult.ok ? `Reply: ${testResult.reply || "(empty)"}` : testResult.error}
              </div>
              {testResult.ok && (
                <div className="text-gray-300">
                  {testResult.latencyMs} ms · {testResult.inTokens} in / {testResult.outTokens} out tokens ·{" "}
                  {testResult.priceKnown ? `≈ ${fmtUsd(testResult.costUsd)}` : "price unknown — set one below"}
                  {" · "}temperature {testResult.supportsTemperature ? "ok" : "not supported (auto-omitted)"}
                  {" · "}max tokens {testResult.supportsMaxTokens ? "ok" : "not supported (auto-omitted)"}
                </div>
              )}
            </div>
          )}

          {/* Budget + price override */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-cyan-300 text-xs font-semibold mb-1 flex items-center gap-1.5">
                <Wallet className="w-3.5 h-3.5" />
                Monthly budget (USD, 0 = no limit)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={cap}
                  onChange={(e) => setCap(e.target.value)}
                  className={field}
                />
                <button
                  type="button"
                  disabled={Number(cap) === s.monthlyCapUsd}
                  onClick={() => patch({ monthlyCapUsd: Math.max(0, Number(cap) || 0) })}
                  className={`${btn} bg-cyan-500/20 text-cyan-200 border-cyan-500/40 hover:bg-cyan-500/30`}
                >
                  Save
                </button>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Warns at 80 %, stops at 100 %. Costs are estimates.</p>
            </div>
            <div>
              <label className="block text-cyan-300 text-xs font-semibold mb-1">
                Price override for the chosen model ($ per 1M tokens)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="in"
                  value={override.in}
                  onChange={(e) => setOverride((o) => ({ ...o, in: e.target.value }))}
                  className={field}
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="out"
                  value={override.out}
                  onChange={(e) => setOverride((o) => ({ ...o, out: e.target.value }))}
                  className={field}
                />
                <button
                  type="button"
                  disabled={!s.provider || !s.model}
                  onClick={saveOverride}
                  className={`${btn} bg-cyan-500/20 text-cyan-200 border-cyan-500/40 hover:bg-cyan-500/30`}
                >
                  Save
                </button>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Leave empty to use the built-in / live price.</p>
            </div>
          </div>

          {/* Scopes */}
          <div>
            <label className="block text-cyan-300 text-xs font-semibold mb-1">What it may read</label>
            <div className="flex flex-wrap gap-1.5">
              {ALL_SCOPES.map((k) => {
                const on = scopes.includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleScope(k)}
                    aria-pressed={on}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      on
                        ? "bg-purple-500/20 text-purple-200 border-purple-400/50"
                        : "bg-gray-700/40 text-gray-400 border-gray-600 hover:text-gray-200"
                    }`}
                  >
                    {SCOPE_LABELS[k]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Voice */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={!!s.tts}
                disabled={!ttsSupported()}
                onChange={(e) => patch({ tts: e.target.checked })}
                className="w-4 h-4 accent-cyan-500"
              />
              <Volume2 className="w-4 h-4 text-cyan-300" />
              Read replies aloud{!ttsSupported() ? " (not supported here)" : ""}
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={!!s.stt}
                disabled={!sttSupported()}
                onChange={(e) => patch({ stt: e.target.checked })}
                className="w-4 h-4 accent-cyan-500"
              />
              <Mic className="w-4 h-4 text-cyan-300" />
              Microphone dictation{!sttSupported() ? " (not supported here)" : ""}
            </label>
            {s.tts && voices.length > 0 && (
              <div className="sm:col-span-2 flex gap-2">
                <select
                  value={s.voice || ""}
                  onChange={(e) => patch({ voice: e.target.value || null })}
                  className={field}
                >
                  <option value="">Default voice</option>
                  {voices.map((v) => (
                    <option key={v.name} value={v.name}>
                      {v.name} ({v.lang})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => speak(`Hi, I am ${s.assistantName || "xyz Ai"}.`, s.voice)}
                  className={`${btn} bg-gray-700 text-cyan-200 border-cyan-500/30 hover:bg-gray-600`}
                >
                  Preview
                </button>
              </div>
            )}
          </div>

          {/* Usage */}
          {usage && (
            <div className="rounded-xl bg-gray-700/40 border border-gray-600 p-3 text-xs text-gray-300 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-cyan-300 font-semibold">Usage this month ({usage.month})</span>
                <button type="button" onClick={loadUsage} className="text-cyan-400 hover:text-cyan-300">
                  refresh
                </button>
              </div>
              <div>
                {usage.calls} call{usage.calls === 1 ? "" : "s"} · {usage.totalIn.toLocaleString()} in /{" "}
                {usage.totalOut.toLocaleString()} out tokens · ≈ {fmtUsd(usage.totalCost)}
                {usage.capUsd > 0 ? ` of ${fmtUsd(usage.capUsd)}` : ""}
                {usage.unknownCostCalls ? ` · ${usage.unknownCostCalls} call(s) with unknown price` : ""}
              </div>
              {usage.byModel.map((m) => (
                <div key={`${m.provider}/${m.model}`} className="text-gray-400">
                  {providerLabel(m.provider)} · {m.model}: {m.calls} calls, {m.inTokens.toLocaleString()}/
                  {m.outTokens.toLocaleString()} tokens, ≈ {fmtUsd(m.costUsd)}
                  {m.unknownCost ? " (+unknown)" : ""}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AiSettingsSection;
